/**
 * Partners API Service
 * Servicio para comunicarse con el backend de Partners
 */

import type {
  Partner,
  PartnerType,
  CreatePartnerRequest,
  CreatePartnerResponse,
  UpdatePartnerRequest,
  UpdatePartnerResponse,
  ListPartnersResponse,
  GetPartnerResponse,
  DeletePartnerResponse,
  RegenerateApiKeyResponse,
  CreatePartnerTypeRequest,
  CreatePartnerTypeResponse,
  UpdatePartnerTypeRequest,
  UpdatePartnerTypeResponse,
  ListPartnerTypesResponse,
} from '../../types/casino-partners';

// Configuración de la API
const API_CONFIG = {
  baseUrl: process.env.BASE_URL || 'http://localhost:5000',
  apiKey: process.env.API_KEY || '',
  adminApiKey: process.env.ADMIN_API_KEY || '',
  timeout: 10000, // 10 segundos
  useProxy: process.env.NEXT_PUBLIC_USE_NBC_PROXY !== 'false',
};

export interface APIError {
  success: false;
  error: string;
  details?: any;
}

/**
 * Realizar request HTTP al backend
 */
async function makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Determinar si usar proxy o llamada directa
  // Si useProxy=false, siempre llamar directamente al backend
  // Si useProxy=true y el endpoint empieza con /api/, usar proxy de Next.js
  const shouldUseProxy = API_CONFIG.useProxy && endpoint.startsWith('/api/');
  
  // Construir URL correcta según el entorno
  let url: string;
  if (shouldUseProxy) {
    // Para el proxy interno, usar la URL base de Next.js
    if (typeof window !== 'undefined') {
      // En el cliente (browser)
      url = `${window.location.origin}${endpoint}`;
    } else {
      // En el servidor (SSR/API routes)
      url = `http://localhost:3000${endpoint}`;
    }
  } else {
    // Para llamadas directas al backend
    url = `${API_CONFIG.baseUrl}${endpoint}`;
  }

  console.log(`🌐 [makeRequest] Starting request:`);
  console.log(`📍 [makeRequest] URL: ${url}`);
  console.log(`📋 [makeRequest] Method: ${options.method || 'GET'}`);
  console.log(`🔄 [makeRequest] Using ${shouldUseProxy ? 'internal proxy' : 'direct backend call'}`);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };
    
    // Solo establecer Content-Type: application/json si no es FormData
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Solo agregar API key para llamadas directas al backend
    if (!shouldUseProxy) {
      headers['x-api-key'] = API_CONFIG.apiKey;
      console.log(`🔑 [makeRequest] API Key: ${API_CONFIG.apiKey.substring(0, 8)}...`);
    }

    const requestOptions: RequestInit = {
      ...options,
      signal: controller.signal,
      headers,
    };
    
    // Para llamadas internas (proxy), no necesitamos configurar CORS
    if (!shouldUseProxy) {
      requestOptions.mode = 'cors';
      requestOptions.credentials = 'omit';
    }

    //console.log(`� [makeRequest] Request headers:`, requestOptions.headers);
    console.log(`📤 [makeRequest] Request body:`, options.body || 'No body');

    const response = await fetch(url, requestOptions);

    clearTimeout(timeoutId);

    console.log(`📡 [makeRequest] Response status: ${response.status} ${response.statusText}`);
    console.log(`📡 [makeRequest] Response headers:`, Object.fromEntries(response.headers.entries()));

    const responseText = await response.text();
    console.log(`� [makeRequest] Response body (first 500 chars):`, responseText.substring(0, 500));

    if (!response.ok) {
      const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      console.error(`❌ [makeRequest] HTTP Error: ${errorMessage}`);
      console.error(`❌ [makeRequest] Response body:`, responseText);
      throw new Error(errorMessage);
    }

    const data = responseText ? JSON.parse(responseText) : {};
    console.log(`✅ [makeRequest] Parsed response:`, data);
    return data as T;
    
  } catch (error: unknown) {
    console.error('❌ [makeRequest] Exception occurred:', error);
    
    if (error instanceof Error) {
      console.error('❌ [makeRequest] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 3).join('\n') // Solo las primeras 3 líneas del stack
      });
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - el servidor no responde');
      }
      throw new Error(`Error de conexión: ${error.message}`);
    }
    
    throw new Error('Error desconocido al comunicarse con el backend');
  }
}

/**
 * Realizar request HTTP para operaciones administrativas
 */
async function makeAdminRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  console.log(`🔐 [makeAdminRequest] Admin operation: ${endpoint}`);
  
  // Obtener token JWT del localStorage
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  
  const headers: Record<string, string> = {
    'X-Admin-API-Key': API_CONFIG.adminApiKey,
    'x-api-key': API_CONFIG.apiKey, // Partner endpoints necesitan ambas claves
    ...options.headers as Record<string, string>,
  };
  
  // Agregar Authorization header con JWT si existe
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log(`🔐 [makeAdminRequest] Added JWT token to request`);
  } else {
    console.warn(`⚠️ [makeAdminRequest] No JWT token found in localStorage`);
  }
  
  // Para FormData, no establecer Content-Type (el navegador lo hace automáticamente)
  const isFormData = options.body instanceof FormData;
  if (isFormData) {
    console.log('🔐 [makeAdminRequest] Detected FormData, removing Content-Type header');
    // Eliminar Content-Type para FormData - el navegador lo establecerá con boundary correcto
    delete headers['Content-Type'];
  }
  
  return makeRequest<T>(endpoint, {
    ...options,
    headers,
  });
}

// ================================
// PARTNERS API
// ================================

/**
 * Listar todos los partners
 */
export async function listPartners(filters?: {
  partner_type?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}): Promise<ListPartnersResponse> {
  console.log('🔍 [PartnersAPI] Listing partners with filters:', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.partner_type) params.append('partner_type', filters.partner_type);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const queryString = params.toString();
    const endpoint = `/api/admin/partners${queryString ? `?${queryString}` : ''}`;

    const response = await makeAdminRequest<ListPartnersResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log(`✅ [PartnersAPI] Listed ${response.partners.length} partners`);
    return response;
  } catch (error) {
    console.error('❌ [PartnersAPI] Error listing partners:', error);
    
    // Si falla el proxy, intentar llamada directa al backend
    console.log('🔄 [PartnersAPI] Trying direct backend call...');
    try {
      const params = new URLSearchParams();
      if (filters?.partner_type) params.append('partner_type', filters.partner_type);
      if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.offset) params.append('offset', String(filters.offset));

      const queryString = params.toString();
      const directUrl = `${API_CONFIG.baseUrl}/api/admin/partners${queryString ? `?${queryString}` : ''}`;
      
      const directResponse = await fetch(directUrl, {
        method: 'GET',
        headers: {
          'X-Admin-API-Key': API_CONFIG.adminApiKey,
          'x-api-key': API_CONFIG.apiKey,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!directResponse.ok) {
        throw new Error(`HTTP ${directResponse.status}: ${directResponse.statusText}`);
      }
      
      const directData = await directResponse.json();
      console.log(`✅ [PartnersAPI] Direct call successful:`, directData);
      return directData;
    } catch (directError) {
      console.error('❌ [PartnersAPI] Direct call also failed:', directError);
      throw error;
    }
  }
}

/**
 * Obtener un partner específico por ID
 */
export async function getPartner(partnerId: string): Promise<GetPartnerResponse> {
  console.log(`🔍 [PartnersAPI] Getting partner: ${partnerId}`);

  try {
    const endpoint = API_CONFIG.useProxy 
      ? `/api/admin/partners/${partnerId}` 
      : `/api/admin/partners/${partnerId}`;

    const response = await makeAdminRequest<GetPartnerResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log(`✅ [PartnersAPI] Retrieved partner: ${partnerId}`);
    return response;
  } catch (error) {
    console.error(`❌ [PartnersAPI] Error getting partner ${partnerId}:`, error);
    throw error;
  }
}

/**
 * Crear nuevo partner
 */
export async function createPartner(partnerData: CreatePartnerRequest): Promise<CreatePartnerResponse> {
  console.log('➕ [PartnersAPI] Creating partner:', partnerData.name);

  try {
    // Preparar datos como JSON (el proxy convertirá a FormData para Flask)
    const jsonData = {
      name: partnerData.name,
      ...(partnerData.email && { email: partnerData.email }),
      partner_type: partnerData.partner_type,
      wallet_address: partnerData.wallet_address,
      settlement_currency: partnerData.settlement_currency || 'USDT',
      display_currency: partnerData.display_currency || 'EUR',
      minimum_amount: String(partnerData.minimum_amount || 1),
      maximum_amount: String(partnerData.maximum_amount || 10000),
      commission_rate: String(partnerData.commission_rate || 0),
      ...(partnerData.webhook_url && { webhook_url: partnerData.webhook_url }),
      ...(partnerData.withdrawal_user_email && { withdrawal_user_email: partnerData.withdrawal_user_email }),
      is_active: String(partnerData.is_active ?? true),
    };

    const response = await makeAdminRequest<CreatePartnerResponse>('/api/admin/partners', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jsonData),
    });
    
    console.log(`✅ [PartnersAPI] Created partner: ${partnerData.name}`);
    return response;
  } catch (error) {
    console.error('❌ [PartnersAPI] Error creating partner:', error);
    throw error;
  }
};

/**
 * Actualizar partner existente
 */
export async function updatePartner(
  partnerId: string, 
  partnerData: UpdatePartnerRequest
): Promise<UpdatePartnerResponse> {
  console.log(`✏️ [PartnersAPI] Updating partner: ${partnerId}`);

  try {
    // Preparar datos como JSON (Flask backend acepta JSON para PUT)
    const jsonData: Record<string, any> = {};
    if (partnerData.name !== undefined) jsonData.name = partnerData.name;
    if (partnerData.email !== undefined) jsonData.email = partnerData.email;
    if (partnerData.wallet_address !== undefined) jsonData.wallet_address = partnerData.wallet_address;
    if (partnerData.settlement_currency !== undefined) jsonData.settlement_currency = partnerData.settlement_currency;
    if (partnerData.display_currency !== undefined) jsonData.display_currency = partnerData.display_currency;
    if (partnerData.minimum_amount !== undefined) jsonData.minimum_amount = String(partnerData.minimum_amount);
    if (partnerData.maximum_amount !== undefined) jsonData.maximum_amount = String(partnerData.maximum_amount);
    if (partnerData.commission_rate !== undefined) jsonData.commission_rate = String(partnerData.commission_rate);
    if (partnerData.webhook_url !== undefined) jsonData.webhook_url = partnerData.webhook_url;
    if (partnerData.withdrawal_user_email !== undefined) jsonData.withdrawal_user_email = partnerData.withdrawal_user_email;
    if (partnerData.is_active !== undefined) jsonData.is_active = partnerData.is_active;

    const response = await makeAdminRequest<UpdatePartnerResponse>(`/api/admin/partners/${partnerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jsonData),
    });
    
    console.log(`✅ [PartnersAPI] Updated partner: ${partnerId}`);
    return response;
  } catch (error) {
    console.error(`❌ [PartnersAPI] Error updating partner ${partnerId}:`, error);
    throw error;
  }
}

/**
 * Eliminar partner
 */
export async function deletePartner(partnerId: string): Promise<DeletePartnerResponse> {
  console.log(`🗑️ [PartnersAPI] Deleting partner: ${partnerId}`);

  try {
    const response = await makeAdminRequest<DeletePartnerResponse>(`/api/admin/partners/${partnerId}`, {
      method: 'DELETE',
    });
    
    console.log(`✅ [PartnersAPI] Deleted partner: ${partnerId}`);
    return response;
  } catch (error) {
    console.error(`❌ [PartnersAPI] Error deleting partner ${partnerId}:`, error);
    
    // Si falla el proxy, intentar llamada directa al backend
    console.log('🔄 [PartnersAPI] Trying direct backend call...');
    try {
      const directUrl = `${API_CONFIG.baseUrl}/api/admin/partners/${partnerId}`;
      
      const directResponse = await fetch(directUrl, {
        method: 'DELETE',
        headers: {
          'X-Admin-API-Key': API_CONFIG.adminApiKey,
          'x-api-key': API_CONFIG.apiKey,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!directResponse.ok) {
        throw new Error(`HTTP ${directResponse.status}: ${directResponse.statusText}`);
      }
      
      const directData = await directResponse.json();
      console.log(`✅ [PartnersAPI] Direct call successful:`, directData);
      return directData;
    } catch (directError) {
      console.error('❌ [PartnersAPI] Direct call also failed:', directError);
      throw error;
    }
  }
}

/**
 * Regenerar API key de partner
 */
export async function regeneratePartnerApiKey(partnerId: string): Promise<RegenerateApiKeyResponse> {
  console.log(`🔄 [PartnersAPI] Regenerating API key for partner: ${partnerId}`);

  try {
    const endpoint = API_CONFIG.useProxy 
      ? `/api/admin/partners/${partnerId}/regenerate-key` 
      : `/api/admin/partners/${partnerId}/regenerate-key`;

    const response = await makeAdminRequest<RegenerateApiKeyResponse>(endpoint, {
      method: 'POST',
    });
    
    console.log(`✅ [PartnersAPI] Regenerated API key for partner: ${partnerId}`);
    return response;
  } catch (error) {
    console.error(`❌ [PartnersAPI] Error regenerating API key for partner ${partnerId}:`, error);
    throw error;
  }
}

// ================================
// PARTNER TYPES API
// ================================

/**
 * Listar todos los tipos de partners
 */
export async function listPartnerTypes(): Promise<ListPartnerTypesResponse> {
  console.log('🔍 [PartnerTypesAPI] Listing partner types');

  try {
    // USAR LLAMADA DIRECTA AL BACKEND (no proxy) para partners
    const endpoint = '/api/admin/partner-types';
    
    const response = await makeAdminRequest<ListPartnerTypesResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log(`✅ [PartnerTypesAPI] Listed ${response.partner_types.length} partner types`);
    return response;
  } catch (error) {
    console.error('❌ [PartnerTypesAPI] Error listing partner types:', error);
    
    // Si falla el proxy, intentar llamada directa al backend
    console.log('🔄 [PartnerTypesAPI] Trying direct backend call...');
    try {
      const directResponse = await fetch(`${API_CONFIG.baseUrl}/api/admin/partner-types`, {
        method: 'GET',
        headers: {
          'X-Admin-API-Key': API_CONFIG.adminApiKey,
          'x-api-key': API_CONFIG.apiKey,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!directResponse.ok) {
        throw new Error(`HTTP ${directResponse.status}: ${directResponse.statusText}`);
      }
      
      const directData = await directResponse.json();
      console.log(`✅ [PartnerTypesAPI] Direct call successful:`, directData);
      return directData;
    } catch (directError) {
      console.error('❌ [PartnerTypesAPI] Direct call also failed:', directError);
      throw error;
    }
  }
}

/**
 * Crear nuevo tipo de partner
 */
export async function createPartnerType(typeData: CreatePartnerTypeRequest): Promise<CreatePartnerTypeResponse> {
  console.log('➕ [PartnerTypesAPI] Creating partner type:', typeData.name);

  try {
    // Preparar datos como JSON (el proxy convertirá a FormData para Flask)
    const jsonData = {
      name: typeData.name,
      description: typeData.description,
      payment_style: typeData.payment_style,
      is_active: String(typeData.is_active ?? true),
    };

    const response = await makeAdminRequest<CreatePartnerTypeResponse>('/api/admin/partner-types', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jsonData),
    });
    
    console.log(`✅ [PartnerTypesAPI] Created partner type: ${typeData.name}`);
    return response;
  } catch (error) {
    console.error('❌ [PartnerTypesAPI] Error creating partner type:', error);
    throw error;
  }
}

/**
 * Actualizar tipo de partner existente
 */
export async function updatePartnerType(
  typeId: string, 
  typeData: UpdatePartnerTypeRequest
): Promise<UpdatePartnerTypeResponse> {
  console.log(`✏️ [PartnerTypesAPI] Updating partner type: ${typeId}`);

  try {
    // Preparar datos como JSON (Flask backend acepta JSON para PUT)
    const jsonData: Record<string, any> = {};
    if (typeData.name !== undefined) jsonData.name = typeData.name;
    if (typeData.description !== undefined) jsonData.description = typeData.description;
    if (typeData.is_active !== undefined) jsonData.is_active = typeData.is_active;

    const response = await makeAdminRequest<UpdatePartnerTypeResponse>(`/api/admin/partner-types/${typeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jsonData),
    });
    
    console.log(`✅ [PartnerTypesAPI] Updated partner type: ${typeId}`);
    return response;
  } catch (error) {
    console.error(`❌ [PartnerTypesAPI] Error updating partner type ${typeId}:`, error);
    throw error;
  }
}

/**
 * Eliminar tipo de partner
 */
export async function deletePartnerType(typeId: string): Promise<{ success: boolean; message: string }> {
  console.log(`🗑️ [PartnerTypesAPI] Deleting partner type: ${typeId}`);

  try {
    const response = await makeAdminRequest<{ success: boolean; message: string }>(`/api/admin/partner-types/${typeId}`, {
      method: 'DELETE',
    });
    
    console.log(`✅ [PartnerTypesAPI] Deleted partner type: ${typeId}`);
    return response;
  } catch (error) {
    console.error(`❌ [PartnerTypesAPI] Error deleting partner type ${typeId}:`, error);
    
    // Si falla el proxy, intentar llamada directa al backend
    console.log('🔄 [PartnerTypesAPI] Trying direct backend call...');
    try {
      const directUrl = `${API_CONFIG.baseUrl}/api/admin/partner-types/${typeId}`;
      
      const directResponse = await fetch(directUrl, {
        method: 'DELETE',
        headers: {
          'X-Admin-API-Key': API_CONFIG.adminApiKey,
          'x-api-key': API_CONFIG.apiKey,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!directResponse.ok) {
        throw new Error(`HTTP ${directResponse.status}: ${directResponse.statusText}`);
      }
      
      const directData = await directResponse.json();
      console.log(`✅ [PartnerTypesAPI] Direct call successful:`, directData);
      return directData;
    } catch (directError) {
      console.error('❌ [PartnerTypesAPI] Direct call also failed:', directError);
      throw error;
    }
  }
}

// ================================
// UTILITIES
// ================================

/**
 * Validar si la API está disponible
 */
export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  try {
    const response = await makeRequest<{ status: string; timestamp: string }>('/api/health');
    return response;
  } catch (error) {
    console.error('❌ [PartnersAPI] Health check failed:', error);
    throw error;
  }
}

/**
 * Obtener estadísticas de partners
 */
export async function getPartnersStats(): Promise<{
  total_partners: number;
  active_partners: number;
  partner_types_count: number;
  transactions_today: number;
}> {
  try {
    const partnersResponse = await listPartners();
    const typesResponse = await listPartnerTypes();
    
    return {
      total_partners: partnersResponse.count,
      active_partners: partnersResponse.partners.filter(p => p.is_active).length,
      partner_types_count: typesResponse.count,
      transactions_today: 0, // TODO: Implementar cuando exista endpoint de stats
    };
  } catch (error) {
    console.error('❌ [PartnersAPI] Error getting stats:', error);
    throw error;
  }
}

// ================================
// SECURE DEPOSIT TOKENS API
// ================================

export interface GenerateTokenRequest {
  order_id: string;
  email: string;
  expiration_seconds?: number;
  frontend_url?: string;
}

export interface GenerateTokenResponse {
  token: string;
  checksum: string;
  redirect_url: string;
  expires_at: string;
}

export interface ValidateTokenRequest {
  token: string;
  order_id: string;
}

export interface ValidateTokenResponse {
  success: boolean;
  order_data?: {
    order_id: string;
    email: string;
    amount: string;
    payment_address: string;
    expires_at: string;
    partner_name: string;
    partner_order_id?: string;
    webhook_url?: string;
    // Se agregan campos opcionales que pueden venir desde el backend
    callback_url?: string;
    cancel_url?: string;
    minimum_amount: string;
    maximum_amount: string;
    user_balance?: string;  // Balance disponible del usuario (para withdrawals)
    public_key?: string;    // Wallet del usuario (para withdrawals)
  };
  error?: string;
}

/**
 * Generar token seguro para depósito
 * NOTA: No se pasa API key desde el cliente - el proxy del servidor la maneja
 */
export async function generateDepositToken(
  tokenData: GenerateTokenRequest
): Promise<GenerateTokenResponse> {
  console.log('🔐 [SecureTokensAPI] Generating deposit token for order:', tokenData.order_id);

  try {
    // Preparar FormData
    const formData = new FormData();
    formData.append('order_id', tokenData.order_id);
    formData.append('email', tokenData.email);
    formData.append('expiration_seconds', String(tokenData.expiration_seconds || 30));

    // NO se envía API key desde el cliente - el proxy del servidor la manejará
    const response = await makeRequest<GenerateTokenResponse>('/api/partner/generate-deposit-token', {
      method: 'POST',
      body: formData,
    });
    
    console.log(`✅ [SecureTokensAPI] Token generated successfully:`, {
      token: response.token.substring(0, 8) + '...',
      redirect_url: response.redirect_url
    });
    return response;
  } catch (error) {
    console.error('❌ [SecureTokensAPI] Error generating token:', error);
    throw error;
  }
}

/**
 * Generar token seguro para flujo de retiro (withdrawal)
 * NOTA: No se pasa API key desde el cliente - el proxy del servidor la maneja
 */
export async function generateWithdrawalToken(
  tokenData: GenerateTokenRequest
): Promise<GenerateTokenResponse> {
  console.log('🔐 [SecureTokensAPI] Generating withdrawal token for order:', tokenData.order_id);

  try {
    // Preparar FormData
    const formData = new FormData();
    formData.append('order_id', tokenData.order_id);
    formData.append('email', tokenData.email);
    formData.append('expiration_seconds', String(tokenData.expiration_seconds || 30));

    // NO se envía API key desde el cliente - el proxy del servidor la manejará
    const response = await makeRequest<GenerateTokenResponse>('/api/partner/generate-withdrawal-token', {
      method: 'POST',
      body: formData,
    });
    
    console.log(`✅ [SecureTokensAPI] Withdrawal token generated successfully:`, {
      token: response.token.substring(0, 8) + '...',
      redirect_url: response.redirect_url
    });
    return response;
  } catch (error) {
    console.error('❌ [SecureTokensAPI] Error generating withdrawal token:', error);
    throw error;
  }
}

/**
 * Validar token seguro de depósito
 * NOTA: No se pasa API key desde el cliente - el proxy del servidor la maneja
 */
export async function validateDepositToken(
  tokenData: ValidateTokenRequest
): Promise<ValidateTokenResponse> {
  console.log('🔍 [SecureTokensAPI] Validating deposit token:', {
    token: tokenData.token.substring(0, 8) + '...',
    order_id: tokenData.order_id.substring(0, 8) + '...'
  });

  try {
    // Preparar FormData (estándar de la aplicación)
    const formData = new FormData();
    formData.append('token', tokenData.token);
    formData.append('order_id', tokenData.order_id);

    // NO se envía API key desde el cliente - el proxy del servidor la manejará
    const response = await makeRequest<ValidateTokenResponse>('/api/partner/validate-deposit-token', {
      method: 'POST',
      body: formData,
    });
    
    console.log(`✅ [SecureTokensAPI] Token validation result:`, {
      success: response.success,
      email: response.order_data?.email,
      amount: response.order_data?.amount,
      callback_url: response.order_data?.callback_url,
      cancel_url: response.order_data?.cancel_url
    });
    return response;
  } catch (error) {
    console.error('❌ [SecureTokensAPI] Error validating token:', error);
    throw error;
  }
}

/**
 * Validar token seguro de retiro (withdrawal)
 * NOTA: Usa el mismo endpoint backend que validateDepositToken
 * La diferencia está en la interpretación de los campos:
 * - payment_address se usa como received_address (wallet destino del usuario)
 * - user_balance indica el máximo disponible para retirar
 */
export async function validateWithdrawalToken(
  tokenData: ValidateTokenRequest
): Promise<ValidateTokenResponse> {
  console.log('🔍 [SecureTokensAPI] Validating withdrawal token:', {
    token: tokenData.token.substring(0, 8) + '...',
    order_id: tokenData.order_id.substring(0, 8) + '...'
  });

  try {
    // Llamar al mismo endpoint - el backend maneja ambos tipos
    const response = await validateDepositToken(tokenData);
    
    console.log(`✅ [SecureTokensAPI] Withdrawal token validation result:`, {
      success: response.success,
      email: response.order_data?.email,
      amount: response.order_data?.amount,
      user_balance: response.order_data?.user_balance,
      public_key: response.order_data?.public_key
    });
    
    return response;
  } catch (error) {
    console.error('❌ [SecureTokensAPI] Error validating withdrawal token:', error);
    throw error;
  }
}

export interface ConfirmEmailRequest {
  order_id: string;
  email: string;
  amount?: number;
}

export interface ConfirmEmailResponse {
  success: boolean;
  message?: string;
  order?: {
    order_id: string;
    user_id: string;
    email: string;
    amount: string | null;
    status: string;
    public_key?: string;  // Wallet destino del usuario (para withdrawals)
  };
  error?: string;
}

/**
 * Confirmar email verificado y actualizar orden con user_id
 * NOTA: No se pasa API key desde el cliente - el proxy del servidor la maneja
 */
export async function confirmEmailVerified(
  data: ConfirmEmailRequest
): Promise<ConfirmEmailResponse> {
  console.log('✅ [SecureTokensAPI] Confirming email verified:', {
    order_id: data.order_id.substring(0, 8) + '...',
    email: data.email,
    amount: data.amount
  });

  try {
    // Preparar FormData (estándar de la aplicación)
    const formData = new FormData();
    formData.append('order_id', data.order_id);
    formData.append('email', data.email);
    if (data.amount !== undefined) {
      formData.append('amount', String(data.amount));
    }

    // NO se envía API key desde el cliente - el proxy del servidor la manejará
    const response = await makeRequest<ConfirmEmailResponse>('/api/partner/confirm-email-verified', {
      method: 'POST',
      body: formData,
    });
    
    console.log(`✅ [SecureTokensAPI] Email confirmation result:`, {
      success: response.success,
      user_id: response.order?.user_id,
      status: response.order?.status
    });
    return response;
  } catch (error) {
    console.error('❌ [SecureTokensAPI] Error confirming email:', error);
    throw error;
  }
}

/**
 * Actualizar partner_order con transaction_group_id para tracking de blockchain
 */
export async function updateTransactionGroup(
  order_id: string,
  transaction_group_id: string
): Promise<{ success: boolean; error?: string; order?: any }> {
  console.log('🔗 [SecureTokensAPI] Updating transaction group:', {
    order_id: order_id.substring(0, 8) + '...',
    transaction_group_id: transaction_group_id.substring(0, 20) + '...'
  });

  try {
    // Timeout de 10 segundos para evitar bloqueos
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    // Usar el proxy del servidor que maneja la API key de forma segura
    const response = await fetch('/api/partner/update-transaction-group', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id,
        transaction_group_id
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const data = await response.json();

    if (!response.ok) {
      console.error('❌ [SecureTokensAPI] Error updating transaction group:', {
        status: response.status,
        statusText: response.statusText,
        data
      });
      return { success: false, error: data.error || 'Failed to update transaction group' };
    }

    console.log('✅ [SecureTokensAPI] Transaction group updated successfully:', data);
    return data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Network error';
    console.error('❌ [SecureTokensAPI] Error updating transaction group:', {
      error: errorMessage,
      isAbortError: error instanceof Error && error.name === 'AbortError',
      order_id,
      transaction_group_id
    });
    
    return { 
      success: false, 
      error: error instanceof Error && error.name === 'AbortError' 
        ? 'Request timeout - order update will be retried automatically'
        : errorMessage
    };
  }
}

// ================================
// DASHBOARD STATISTICS API
// ================================

export interface DashboardStats {
  total_deposits_today: string;
  active_orders: number;
  registered_users: number;
  failed_transactions: number;
  recent_orders: Array<{
    id: string;
    partner_order_id?: string;
    type: string;
    status: string;
    amount: number;
    created_at: string;
    partner_name?: string;
    user_email?: string;
  }>;
  recent_transactions: Array<{
    id: string;
    amount: number;
    status: string;
    created_at: string;
    user_email?: string;
    partner_name?: string;
  }>;
}

export interface DashboardStatsResponse {
  success: boolean;
  stats: DashboardStats;
  error?: string;
}

/**
 * Obtener estadísticas del dashboard filtradas por rol
 * - Admin: Ve todas las estadísticas globales
 * - Partner: Ve solo estadísticas de su partner
 * - User: Ve solo sus estadísticas personales
 */
export async function getDashboardStats(): Promise<DashboardStatsResponse> {
  console.log('📊 [DashboardAPI] Getting dashboard statistics');

  try {
    const response = await makeAdminRequest<DashboardStatsResponse>('/api/admin/dashboard-stats', {
      method: 'GET',
    });
    
    console.log('✅ [DashboardAPI] Dashboard stats retrieved successfully');
    return response;
  } catch (error) {
    console.error('❌ [DashboardAPI] Error getting dashboard stats:', error);
    throw error;
  }
}

// ================================
// ORDERS API
// ================================

export interface Order {
  id: string;
  partner_order_id?: string;
  user_id: string;
  partner_id: string;
  type: 'deposit' | 'withdrawal';
  amount: number;
  status: 'pending' | 'pending_approval' | 'processing' | 'completed' | 'failed' | 'expired' | 'paused' | 'rejected';
  created_at: string;
  updated_at: string;
  admin_notes?: string;
  // Información adicional del JOIN
  user_email?: string;
  user_name?: string;
  partner_name?: string;
}

export interface OrderFilters {
  status?: string;
  type?: string;
  partner_id?: string;
  user_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface OrdersResponse {
  success: boolean;
  orders: Order[];
  total?: number;
  error?: string;
}

export interface OrderResponse {
  success: boolean;
  order: Order;
  error?: string;
}

export interface UpdateOrderStatusResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Obtener lista de órdenes filtradas por rol
 * - Admin: Ve todas las órdenes
 * - Partner: Ve solo órdenes de su partner
 * - User: Ve solo sus órdenes personales
 */
export async function getOrders(filters?: OrderFilters): Promise<OrdersResponse> {
  console.log('📋 [OrdersAPI] Getting orders with filters:', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.partner_id) params.append('partner_id', filters.partner_id);
    if (filters?.user_id) params.append('user_id', filters.user_id);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const queryString = params.toString();
    const endpoint = `/api/admin/orders${queryString ? `?${queryString}` : ''}`;

    const response = await makeAdminRequest<OrdersResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log('✅ [OrdersAPI] Orders retrieved successfully:', response.orders?.length || 0);
    return response;
  } catch (error) {
    console.error('❌ [OrdersAPI] Error getting orders:', error);
    throw error;
  }
}

/**
 * Obtener una orden específica por ID
 */
export async function getOrder(orderId: string): Promise<Order> {
  console.log('🔍 [OrdersAPI] Getting order details for ID:', orderId);

  try {
    const response = await makeAdminRequest<OrderResponse>(`/api/admin/orders/${orderId}`, {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to get order');
    }
    
    console.log('✅ [OrdersAPI] Order retrieved successfully:', response.order.id);
    return response.order;
  } catch (error) {
    console.error('❌ [OrdersAPI] Error getting order:', error);
    throw error;
  }
}

/**
 * Actualizar estado de una orden (solo Admin)
 */
export async function updateOrderStatus(orderId: string, status: string, transaction_hash?: string): Promise<boolean> {
  console.log('⚡ [OrdersAPI] Updating order status:', { orderId, status, transaction_hash });

  try {
    const body: any = { status };
    if (transaction_hash) {
      body.transaction_hash = transaction_hash;
    }
    
    const response = await makeAdminRequest<UpdateOrderStatusResponse>(`/api/admin/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to update order status');
    }
    
    console.log('✅ [OrdersAPI] Order status updated successfully');
    return true;
  } catch (error) {
    console.error('❌ [OrdersAPI] Error updating order status:', error);
    throw error;
  }
}

// ================================
// TRANSACTIONS API
// ================================

export interface Transaction {
  id: string;
  sender_address: string;
  recipient_address: string;
  amount: number;
  network: string;
  txn_hash?: string;
  transaction_type?: string;
  transaction_group_id?: string;
  status?: string; // Puede ser transaction_status
  transaction_status?: string;
  created_at: string;
  updated_at?: string;
  retry_count?: number;
  last_error?: string;
  nonce?: string;
  signed_at?: string;
  // Información adicional para contexto
  user_email?: string;
  partner_name?: string;
  wallet_address?: string;
  // Información de órdenes relacionadas
  order_id?: string;
  partner_order_id?: string;
  order_status?: string;
  order_type?: string;
  order_amount?: number;
  // Dirección de la transacción (sent/received)
  direction?: 'sent' | 'received' | 'unknown';
}

export interface TransactionFilters {
  status?: string;
  network?: string;
  transaction_type?: string;
  search?: string;
  transaction_group_id?: string;
  direction?: string;
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}

export interface TransactionsResponse {
  success: boolean;
  transactions: Transaction[];
  total?: number;
  error?: string;
}

export interface TransactionResponse {
  success: boolean;
  transaction: Transaction;
  error?: string;
}

/**
 * Obtener lista de transacciones filtradas por rol
 * - Admin: Ve todas las transacciones
 * - Partner: Ve transacciones relacionadas con sus usuarios
 * - User: Ve solo sus transacciones personales
 */
export async function getTransactions(filters?: TransactionFilters): Promise<TransactionsResponse> {
  console.log('💳 [TransactionsAPI] Getting transactions with filters:', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.network) params.append('network', filters.network);
    if (filters?.transaction_type) params.append('transaction_type', filters.transaction_type);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.transaction_group_id) params.append('transaction_group_id', filters.transaction_group_id);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const queryString = params.toString();
    const endpoint = `/api/admin/transactions${queryString ? `?${queryString}` : ''}`;

    const response = await makeAdminRequest<TransactionsResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log('✅ [TransactionsAPI] Transactions retrieved successfully:', response.transactions?.length || 0);
    return response;
  } catch (error) {
    console.error('❌ [TransactionsAPI] Error getting transactions:', error);
    throw error;
  }
}

/**
 * Obtener una transacción específica por ID
 */
export async function getTransaction(transactionId: string): Promise<Transaction> {
  console.log('🔍 [TransactionsAPI] Getting transaction details for ID:', transactionId);

  try {
    const response = await makeAdminRequest<TransactionResponse>(`/api/admin/transactions/${transactionId}`, {
      method: 'GET',
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to get transaction');
    }
    
    console.log('✅ [TransactionsAPI] Transaction retrieved successfully:', response.transaction.id);
    return response.transaction;
  } catch (error) {
    console.error('❌ [TransactionsAPI] Error getting transaction:', error);
    throw error;
  }
}

// ================================
// ANALYTICS API
// ================================

export interface AnalyticsOverview {
  period: string;
  date_range: {
    start: string;
    end: string;
  };
  orders_by_status?: Record<string, { count: number; amount: number }>;
  orders_by_type?: Record<string, { count: number; amount: number }>;
  transactions_by_network?: Record<string, { count: number; amount: number }>;
  top_partners?: Array<{
    partner_name: string;
    order_count: number;
    total_volume: number;
  }>;
  top_users?: Array<{
    email: string;
    order_count: number;
    total_volume: number;
  }>;
  orders_by_partner?: Array<{
    partner_name: string;
    order_count: number;
    total_volume: number;
  }>;
  success_rates?: {
    total_orders: number;
    successful_orders: number;
    failed_orders: number;
  };
}

export interface AnalyticsTrends extends Array<{
  date: string;
  order_count?: number;
  total_volume?: number;
  total_orders?: number;
  successful_orders?: number;
  success_rate?: number;
}> {}

export interface AnalyticsPerformance {
  general_metrics?: {
    total_orders: number;
    completed_orders: number;
    failed_orders: number;
    avg_order_amount: number;
    total_volume: number;
  };
  partner_metrics?: {
    total_orders: number;
    completed_orders: number;
    failed_orders: number;
    avg_order_amount: number;
    total_volume: number;
  };
  user_metrics?: {
    total_orders: number;
    completed_orders: number;
    failed_orders: number;
    avg_order_amount: number;
    total_volume: number;
  };
  top_partners_performance?: Array<{
    partner_name: string;
    order_count: number;
    total_volume: number;
    avg_amount: number;
  }>;
}

export interface AnalyticsOverviewFilters {
  period?: string;
  start_date?: string;
  end_date?: string;
  partner_id?: string;  // Para admin: filtrar por partner específico
}

export interface AnalyticsTrendsFilters {
  type?: string;
  period?: string;
  days?: number;
  partner_id?: string;
}

export interface AnalyticsOverviewResponse {
  success: boolean;
  analytics: AnalyticsOverview;
  filters?: AnalyticsOverviewFilters;
  error?: string;
}

export interface AnalyticsTrendsResponse {
  success: boolean;
  trends: AnalyticsTrends;
  filters?: AnalyticsTrendsFilters;
  error?: string;
}

export interface AnalyticsPerformanceResponse {
  success: boolean;
  performance: AnalyticsPerformance;
  error?: string;
}

/**
 * Obtener resumen de analytics filtrado por rol
 * - Admin: Analytics globales
 * - Partner: Analytics del partner
 * - User: Analytics personales
 */
export async function getAnalyticsOverview(filters?: AnalyticsOverviewFilters): Promise<AnalyticsOverviewResponse> {
  console.log('📊 [AnalyticsAPI] Getting analytics overview with filters:', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.period) params.append('period', filters.period);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.partner_id) params.append('partner_id', filters.partner_id);

    const queryString = params.toString();
    const endpoint = `/api/admin/analytics/overview${queryString ? `?${queryString}` : ''}`;

    const response = await makeAdminRequest<AnalyticsOverviewResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log('✅ [AnalyticsAPI] Analytics overview retrieved successfully');
    return response;
  } catch (error) {
    console.error('❌ [AnalyticsAPI] Error getting analytics overview:', error);
    throw error;
  }
}

/**
 * Obtener tendencias para gráficos
 */
export async function getAnalyticsTrends(filters?: AnalyticsTrendsFilters): Promise<AnalyticsTrendsResponse> {
  console.log('📈 [AnalyticsAPI] Getting analytics trends with filters:', filters);

  try {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.period) params.append('period', filters.period);
    if (filters?.days) params.append('days', String(filters.days));
    if (filters?.partner_id) params.append('partner_id', filters.partner_id);

    const queryString = params.toString();
    const endpoint = `/api/admin/analytics/trends${queryString ? `?${queryString}` : ''}`;

    const response = await makeAdminRequest<AnalyticsTrendsResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log('✅ [AnalyticsAPI] Analytics trends retrieved successfully:', response.trends?.length || 0);
    return response;
  } catch (error) {
    console.error('❌ [AnalyticsAPI] Error getting analytics trends:', error);
    throw error;
  }
}

/**
 * Obtener métricas de rendimiento
 */
export async function getAnalyticsPerformance(partnerId?: string): Promise<AnalyticsPerformanceResponse> {
  console.log('🎯 [AnalyticsAPI] Getting analytics performance', partnerId ? `for partner: ${partnerId}` : '(aggregated)');

  try {
    const params = new URLSearchParams();
    if (partnerId && partnerId !== 'all') {
      params.append('partner_id', partnerId);
    }

    const queryString = params.toString();
    const endpoint = `/api/admin/analytics/performance${queryString ? `?${queryString}` : ''}`;

    const response = await makeAdminRequest<AnalyticsPerformanceResponse>(endpoint, {
      method: 'GET',
    });
    
    console.log('✅ [AnalyticsAPI] Analytics performance retrieved successfully');
    return response;
  } catch (error) {
    console.error('❌ [AnalyticsAPI] Error getting analytics performance:', error);
    throw error;
  }
}

// ==================== USER VALIDATION ====================

export interface CheckUserEmailResponse {
  success: boolean;
  exists: boolean;
  user?: {
    id: number;
    email: string;
    name?: string;
    surname?: string;
    wallet_address?: string;
  };
  message?: string;
  error?: string;
}

/**
 * Verificar si un email existe en la tabla users
 */
export async function checkUserEmail(email: string): Promise<CheckUserEmailResponse> {
  console.log('📧 [UsersAPI] Checking if email exists:', email);

  try {
    const response = await makeAdminRequest<CheckUserEmailResponse>(
      `/api/nbc-api/users/check-email?email=${encodeURIComponent(email)}`,
      {
        method: 'GET',
      }
    );
    
    console.log(`✅ [UsersAPI] Email check complete. Exists: ${response.exists}`);
    return response;
  } catch (error) {
    console.error('❌ [UsersAPI] Error checking email:', error);
    throw error;
  }
}